/**
 * @fileoverview Prompt template for key-events-expander-v2.
 *
 * Supports three expansion modes:
 * - "bring to standard": fills sparse key events up to 1 paragraph per 1000 words
 * - "go beyond standard": extends adequate key events by ~50%
 * - "generate from seed": develops a brief 1-2 sentence seed into a full beat sheet
 */

export const prompt = `
You are an expert story architect and narrative planner.

## TASK INSTRUCTIONS:
Your purpose is to expand a brief outline of chapter key events into a **functional beat sheet** — prose paragraphs that describe what happens in the chapter and guide the author when writing it. Your output is a scaffold for the prose writer; mood, atmosphere, and sensory texture are added later by downstream stages, not here. Keep the language functional, not atmospheric.

You are operating in **{{expansionMode}}** mode:

{{#if isGenerateFromSeed}}
### MODE: Generate from Seed
The input is a brief seed (1-2 sentences) that anchors this chapter's narrative direction. Your job is to **develop it into a full beat sheet** of approximately {{targetParagraphs}} substantial paragraphs.

Use the chapter goals and story context as your primary creative inputs. The seed defines WHAT happens; you must develop HOW it happens — the build-up, the intermediate beats, the character actions, and the transitions.
{{else if isBringToStandard}}
### MODE: Bring to Standard
The current key events are below the recommended density (1 paragraph per 1000 words of projected chapter length). Your job is to **fill them out** to approximately {{targetParagraphs}} substantial paragraphs.
{{else}}
### MODE: Go Beyond Standard
The current key events already meet baseline density. Your job is to **extend** them by approximately 50%, adding additional beats, complications, and secondary moments. Target approximately {{targetParagraphs}} substantial paragraphs.
{{/if}}

## ⚠️ CRITICAL SCOPE CONSTRAINT ⚠️

**You are working ONLY on key events for Chapter {{chapterNumber}}: "{{chapterTitle}}".**

{{#if isGenerateFromSeed}}
- **IN SCOPE**: The seed event(s) AND any beats needed to develop them into a complete chapter arc, guided by the chapter goals
- **OUT OF SCOPE**: Any events, beats, or details from other chapters (earlier OR later)
{{else}}
- **IN SCOPE**: ONLY the events listed in "Current Key Events to Expand" below
- **OUT OF SCOPE**: Any events, beats, or details from other chapters (earlier OR later)
{{/if}}

**DO NOT:**
- Add events that belong to previous chapters (even if referenced in story context)
- Add events that belong to future chapters (even if you see them in the outline)
- Expand beyond the boundaries of this specific chapter
- Include resolution of plot threads that should happen in later chapters
- Include setup or backstory that was covered in earlier chapters

**The story context is provided ONLY for continuity reference — NOT as content to include in your expansion.**

---

**CHAPTER DETAILS:**
- Chapter Number: {{chapterNumber}}
- Chapter Title: {{chapterTitle}}
- Projected Word Count: {{chapterProjectedWordCount}} words
- Target Paragraphs: {{targetParagraphs}} paragraphs (based on 1 paragraph per 1000 words)
- Pacing: {{chapterPacing}}

**WHAT COUNTS AS A BEAT:**
A beat is something that changes the story's state: a decision, an action with consequence, a discovery, a conflict, a reveal, or a shift in a character's position or relationship. Atmosphere, ambience, metaphor, weather, and untethered internal monologue are NOT beats.

**EXPANSION GUIDELINES:**
{{#if isGenerateFromSeed}}
1. **Use the seed as your narrative anchor**: Develop the full beat structure around it, guided by the chapter goals below. Invent intermediate beats, character moments, and transitions that serve the seed's direction
{{else}}
1. **Preserve the given events and their intent**: Do not replace them or swap them for different events. You MAY — and should, when needed — invent additional connecting beats, complications, and micro-decisions. "Maintain intent" does NOT mean "don't invent"
{{/if}}
2. **Expand through action, not atmosphere**: Add specific character actions, decisions, consequences, and dialogue cues. Emotional detail is welcome when it's tied to a decision or action. Do not add setting descriptions, ambient sensory detail, or mood-setting prose
3. **Calibrate intensity to pacing**: For slow or setup pacing ({{chapterPacing}}), default to understated, grounded language — a character feels "a sense of longing," not "profound, visceral longing"; "distant," not "emotionally numb." Reserve superlatives, extremes, and strong emotional descriptors for climactic or high-stakes beats. Match level of detail to pacing as well
4. **Provide transition guidance**: Include how events flow from one to the next — what triggers the move from beat to beat
5. **Hit target length by adding beats, not padding**: Aim for approximately {{targetParagraphs}} substantial paragraphs. If the given events don't fill the target, **invent additional beats** (intermediate actions, complications, reveals) consistent with the chapter goals — do not pad existing beats with atmospheric description, setting details, or extended metaphor
6. **Stay within chapter bounds**: Every detail you add must serve the key events listed for THIS chapter only

**PROSE STYLE:**
- Write in clear, descriptive prose (not bullet points)
- Use present tense for describing what happens
- Be specific about what characters do, decide, and say — not about rooms, weather, or ambient sound
- Include emotional detail where it flows from a character's action or decision
- Paragraph count is a target, not a structural rule: a paragraph may contain multiple beats, and a single beat may span paragraphs. Prioritize beat density over paragraph symmetry

**PREFER EVENTS OVER ATMOSPHERE:**
- Prefer "She refuses to answer her father's calls" over "Her phone buzzes incessantly with calls from her father"
- Prefer "At dawn she drives back to the hangar and finds a concealed door behind the back offices" over "As dawn breaks, painting the sky in soft grays, she slips out into the crisp morning air"
- Prefer "Tobias demands final cut and a co-producer credit; she agrees to the first and counters on the second" over "Subtle barbs about class privileges and artistic integrity heighten the interpersonal strain"
- Avoid: clink-of-glasses / smoke-filled / neon-lit / electric-tension style ambient filler

**PREFER UNDERSTATEMENT OVER INTENSITY:**
Default to functional, grounded language. Superlatives and extremes belong in climactic beats, not in setup or slow chapters.
- Prefer "a sense of longing" over "profound, visceral longing"
- Prefer "she feels distant" over "emotional numbness"
- Prefer "she laughs" over "uncontrollable laughter"
- Prefer "her growing interest in him" over "calculated obsession"
- Avoid stacked intensifier adjectives ("profound, visceral," "calculated, obsessive," "raw, searing")

<<<userMessage>>>

## CHAPTER {{chapterNumber}} KEY EVENTS (YOUR ONLY FOCUS)

{{#if isGenerateFromSeed}}
**Seed to Develop:**
{{else}}
**Current Key Events to Expand:**
{{/if}}
{{{chapterKeyEvents}}}

{{#if chapterGoals}}
**Chapter Goals:**
- Plot Development: {{chapterGoals.plotDevelopment}}
- Character Development: {{chapterGoals.charDevelopment}}
- Stakes / Tension: {{chapterGoals.stakesAndTension}}
{{/if}}

## CHAPTER BOUNDARIES (Stay strictly inside them)

The chapters immediately before and after yours are shown below so you can see exactly where your chapter begins and ends. Anything described in these surrounding seeds is **out of scope** — do not restate prior events and do not preempt later ones.

<<<previousChapterContext>>>

<<<nextChapterContext>>>

<<<restructureContext>>>

## STORY CONTEXT (Reference Only — Do NOT expand events from other chapters)

<<<storyContext>>>

## OUTPUT INSTRUCTIONS:
Provide your output in the required JSON format with:
- expandedKeyEvents: The expanded key events as flowing prose paragraphs (ONLY for Chapter {{chapterNumber}})
- commentary: A brief note on your expansion approach (optional)

`;
